import type { TimelineItem, UseEnhancedTimelineOptions } from "./timeline-types";

/**
 * Pure client-side filter → sort → limit pipeline for timeline items.
 *
 * Extracted verbatim from `timeline-fetchers.ts` so the branch logic can be
 * unit-tested in isolation. Behavior is intentionally identical: the fetchers
 * re-import this and apply it after building the raw timeline (and pass
 * `skipLimit=true` when the database already applied the limit).
 */
export const applyFiltersSortAndLimit = (
  timeline: TimelineItem[],
  options: UseEnhancedTimelineOptions,
  skipLimit = false
): TimelineItem[] => {
  let filteredTimeline = [...timeline];

  if (options.filters) {
    const { search, type, dateFrom, dateTo, status, direction } = options.filters;

    if (search) {
      const searchLower = search.toLowerCase();
      filteredTimeline = filteredTimeline.filter(
        (item) =>
          item.title.toLowerCase().includes(searchLower) ||
          item.body.toLowerCase().includes(searchLower)
      );
    }

    if (type && type.length > 0) {
      filteredTimeline = filteredTimeline.filter((item) => type.includes(item.type));
    }

    if (dateFrom) {
      filteredTimeline = filteredTimeline.filter(
        (item) => new Date(item.occurred_at) >= new Date(dateFrom)
      );
    }

    if (dateTo) {
      filteredTimeline = filteredTimeline.filter(
        (item) => new Date(item.occurred_at) <= new Date(dateTo)
      );
    }

    if (status && status.length > 0) {
      filteredTimeline = filteredTimeline.filter(
        (item) => item.status && status.includes(item.status)
      );
    }

    if (direction && direction.length > 0) {
      filteredTimeline = filteredTimeline.filter(
        (item) => item.direction && direction.includes(item.direction)
      );
    }
  }

  const sortField = options.sort?.field || "occurred_at";
  const sortDirection = options.sort?.direction || "desc";

  filteredTimeline.sort((a, b) => {
    let aValue: string | number = a[sortField];
    let bValue: string | number = b[sortField];

    if (sortField === "occurred_at" || sortField === "created_at") {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  if (!skipLimit && options.limit) {
    filteredTimeline = filteredTimeline.slice(0, options.limit);
  }

  return filteredTimeline;
};
